import { HttpHeaders, HttpParams, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { filter, map, catchError } from 'rxjs/operators';
import { RestRequest, HttpMethod } from './rest-request';
import type { HttpClient, HttpEvent } from '@angular/common/http';

import type { Observable } from 'rxjs';

import type { BackendError } from '../../domain/error.domain';
import type { RestFrameworkService } from './rest-framework.service';
import type { RestInterpolatorService } from './rest-interpolator.service';
import type { RestResourceDescriptor, RestParameterDescriptor, RestResourceConfiguration } from './rest.service';
import type { RestJsonCodecService, DateDecode } from './rest-json-codec.service';
import type { RestOptions, RestHeaders, RestParams } from './rest-request';

export interface RestEndPointConfiguration {
  id: string;
  baseUrl?: string;
  descriptor?: RestResourceDescriptor | RestResourceConfiguration;
}

interface HttpOptions {
  headers?: HttpHeaders;
  observe?: 'body' | 'response';
  params?: HttpParams;
  responseType?: 'json' | 'text' | 'blob' | null;
  warningCategory?: string;
}

type ObservableResponse<T> = Observable<HttpResponse<T> | Observable<never> | HttpResponse<unknown>>;

export class RestResourceService {

  private readonly resourceId: string;
  private readonly baseUrl: string;
  private readonly resourceDescriptor: RestResourceConfiguration;

  constructor(protected httpClient: HttpClient,
              protected restFrameworkService: RestFrameworkService,
              protected restInterpolatorService: RestInterpolatorService,
              protected restJsonCodecService: RestJsonCodecService,
              configuration: RestEndPointConfiguration) {
    this.baseUrl = configuration.baseUrl;
    if (configuration.descriptor !== undefined && this.resourceIsDescriptor(configuration.descriptor)) {
      this.resourceDescriptor = this.convertDescriptorToConfiguration(configuration.descriptor);
    } else if (configuration.descriptor !== undefined) {
      this.resourceDescriptor = configuration.descriptor as RestResourceConfiguration;
    }
    this.resourceId = configuration.id;
  }

  get<T>(restOptions?: RestOptions): Observable<T>;

  get<T>(restOptions?: RestOptions): ObservableResponse<T> {
    return this.request(HttpMethod.GET, null, restOptions);
  }

  post<T, U>(body: T | undefined, restOptions?: RestOptions): Observable<U>;

  post<T>(body: T | undefined, restOptions?: RestOptions): ObservableResponse<T> {
    return this.request(HttpMethod.POST, body, restOptions);
  }

  put<T, U>(body: T | undefined, restOptions?: RestOptions): Observable<U>;

  put<T>(body: T | undefined, restOptions?: RestOptions): ObservableResponse<T> {
    return this.request(HttpMethod.PUT, body, restOptions);
  }

  patch<T>(body: T | undefined, restOptions?: RestOptions): Observable<T>;

  patch<T>(body: T | undefined, restOptions?: RestOptions): ObservableResponse<T> {
    return this.request(HttpMethod.PATCH, body, restOptions);
  }

  del<T>(body: T | undefined, restOptions?: RestOptions): Observable<T>;

  del<T>(body: T | undefined, restOptions?: RestOptions): ObservableResponse<T> {
    return this.request(HttpMethod.DELETE, body, restOptions);
  }

  public request<T>(httpMethod: HttpMethod, body: T | undefined, restOptions?: RestOptions): ObservableResponse<T> {
    const options: HttpOptions = this.convertVerifyRequestOptions(httpMethod, restOptions);
    const pathVariables = (restOptions !== undefined && restOptions.pathVariables !== undefined ? restOptions.pathVariables : undefined);
    const url: string = this.getUrl(httpMethod, pathVariables);
    const request: RestRequest<T> = new RestRequest<T>(httpMethod, url, body, this.restJsonCodecService, options);
    const responseType = restOptions === undefined ? undefined : restOptions.responseType;
    const responseDateDecode = restOptions === undefined ? undefined : restOptions.responseDateDecode;

    return this.httpClient
      .request(request)
      .pipe(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        filter((event: HttpEvent<unknown>): boolean => event instanceof HttpResponse),
        map((response: HttpResponse<T>): HttpResponse<T> => {
          const deserializedBody = this.deserializeBody(response.body, responseType, responseDateDecode);
          if (options.observe === 'body') {
            return deserializedBody as unknown as HttpResponse<T>;
          }
          return response.clone({ body: deserializedBody } as Record<string, T>);
        }),
        catchError((error: HttpErrorResponse): Observable<never> => {
          if (error.error instanceof ErrorEvent) { // network error - we did not get through  the backend
            return throwError(() => error);
          }
          const errors = this.deserializeError(error.error, responseType, responseDateDecode);
          if (options.observe === 'body') {
            return throwError(() => errors);
          }
          return throwError(() => new HttpErrorResponse({
            error: errors,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          }));
        })
      );

  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private resourceIsDescriptor(object: any): object is RestResourceDescriptor {
    return 'methods' in object;
  }

  private convertDescriptorToConfiguration(descriptor: RestResourceDescriptor): RestResourceConfiguration {
    if (descriptor.methods === undefined) {
      return null;
    } else {
      const result: RestResourceConfiguration = {};
      descriptor.methods.forEach((method: string): void => {
        result[method] = {
          href: descriptor.href,
          requestParameters: descriptor.requestParameters,
          pathVariables: descriptor.pathVariables,
          methods: descriptor.methods
        };
      });
      return result;
    }
  }

  private deserializeBody<T>(body: T, responseType?: string, responseDateDecode?: DateDecode): Record<string, T> | T {
    const deserializeBody = responseType === undefined || responseType === 'json'
      ? this.restJsonCodecService.decode(body, responseDateDecode) : body;
    return this.restFrameworkService.unwrapResponse(deserializeBody as Record<string, T>);
  }

  private deserializeError<T>(error: T, responseType?: string, responseDateDecode?: DateDecode): Array<BackendError> {
    const deserializedError = responseType === undefined || responseType === 'json'
      ? this.restJsonCodecService.decode(error, responseDateDecode) : error;
    return this.restFrameworkService.getErrors(deserializedError as Record<string, T>);
  }

  private convertVerifyRequestOptions(method: HttpMethod, restOptions: RestOptions = {}): HttpOptions | never {
    const options: HttpOptions = this.convertOptions(restOptions);

    if (this.resourceDescriptor !== undefined) {
      this.isMethodSupported(method);
      this.isRequiredParametersSpecified(method, options.params);
      if (restOptions !== undefined && restOptions.pathVariables !== undefined && Object.keys(restOptions.pathVariables).length > 0) {
        this.isRequiredPathVariablesSpecified(method, restOptions.pathVariables);
      }
    }
    options.params = this.restFrameworkService.augmentParams(options.params);
    options.headers = this.restFrameworkService.addHeaders(options.headers);
    return options;
  }

  private convertOptions(restOptions: RestOptions): HttpOptions {
    return {
      headers: this.convertHeaders(restOptions.headers),
      observe: restOptions.observe === undefined ? 'body' : restOptions.observe,
      params: this.convertParams(restOptions.params),
      responseType: this.convertResponseType(restOptions.responseType),
      warningCategory: restOptions.warningCategory
    } as HttpOptions;
  }

  private convertResponseType(responseType: string): string {
    if (responseType === undefined || responseType === 'json') {
      return 'text';
    }
    return responseType;
  }

  private convertHeaders(restHeaders: RestHeaders): HttpHeaders {
    let result: HttpHeaders = new HttpHeaders();

    if (restHeaders !== undefined) {
      Object.keys(restHeaders).forEach((key: string): void => {
        result = result.set(key, restHeaders[key]);
      });
    }
    return result;
  }

  private convertParams(restParams: RestParams): HttpParams {
    let result: HttpParams = new HttpParams();

    if (restParams !== undefined) {
      Object.keys(restParams).forEach((key: string): void => {
        const value = restParams[key];
        if (Array.isArray(value)) {
          value.forEach((item: unknown): HttpParams => result = this.addValue(result, key, item));
        } else {
          result = this.addValue(result, key, value);
        }
      });
    }
    return result;
  }

  private addValue(httpParams: HttpParams, key: string, value: unknown): HttpParams {
    if (value !== undefined && value !== null) {
      if (httpParams.has(key)) {
        httpParams = httpParams.append(key, this.restInterpolatorService.formatParameter(value));
      } else {
        httpParams = httpParams.set(key, this.restInterpolatorService.formatParameter(value));
      }
    }
    return httpParams;
  }

  private isMethodSupported(method: HttpMethod): void {
    const resourceDescriptorKeys = Object.keys(this.resourceDescriptor);
    const methods: Array<string> = resourceDescriptorKeys === undefined ? [] : resourceDescriptorKeys;

    if (methods.length > 0 && methods.filter((value: string): boolean => value === method.toString()).length === 0) {
      throw new Error('Method is not supported by resource: ' + method);
    }
  }

  private isRequiredParametersSpecified(method: HttpMethod, httpParams: HttpParams): void {
    if (httpParams !== undefined) {
      const parameterDescriptors = this.getParameterDescriptors(method);

      parameterDescriptors.forEach((descriptor: RestParameterDescriptor): void => {
        const value: string | null = httpParams.get(descriptor.name);

        if (descriptor.required && value === null) {
          throw new Error(`Required parameter is missing: ${descriptor.name}`);
        }
      });
    }
  }

  private isRequiredPathVariablesSpecified(method: HttpMethod, pathVariables: RestParams): void {
    if (pathVariables !== undefined) {
      const parameterDescriptors = this.getPathVariableDescriptors(method);

      parameterDescriptors.forEach((descriptor: RestParameterDescriptor): void => {
        const value = pathVariables[descriptor.name];

        if (descriptor.required && value === null) {
          throw new Error(`Required path variable is missing: ${descriptor.name}`);
        }
      });
    }
  }

  private getParameterDescriptors(method: HttpMethod): Array<RestParameterDescriptor> {
    return this.resourceDescriptor[method].requestParameters === undefined ? [] : this.resourceDescriptor[method].requestParameters;
  }

  private getPathVariableDescriptors(method: HttpMethod): Array<RestParameterDescriptor> {
    return this.resourceDescriptor[method].pathVariables === undefined ? [] : this.resourceDescriptor[method].pathVariables;
  }

  private getUrl(httpMethod: HttpMethod, pathVariables: RestParams): string {
    if (this.resourceDescriptor !== undefined) {
      return this.restInterpolatorService.interpolate(
        this.resourceDescriptor[httpMethod],
        this.baseUrl,
        pathVariables
      );
    }
    return this.resourceId;
  }
}
